// Entry point: landing screen, presenter controls and the flow hook → story.

import qrcode from 'qrcode-generator';
import { api } from './api.js';
import { sfx } from './audio.js';
import { backstage } from './backstage.js';
import { Story } from './story.js';
import { runSignal } from './tasks/signal.js';
import { askToInstall, detect, isStandalone } from './install.js';
import { clearBadge, deliveryResult, explainFailure, onPushOpen, permission, pushSupport, schedule, subscribe } from './push.js';
import { $, el, settings, sleep } from './util.js';

const views = {
  landing: $('#view-landing'),
  signal: $('#view-signal'),
  chat: $('#view-chat'),
};
function show(name: keyof typeof views) {
  for (const [k, v] of Object.entries(views)) v.classList.toggle('is-active', k === name);
}

// --- Presenter controls (outside the fiction; duplicated in the backstage panel on phones) ---
const all = (action: string) => [...document.querySelectorAll<HTMLButtonElement>(`[data-action="${action}"]`)];
const on = (action: string, fn: () => void) => all(action).forEach((b) => b.addEventListener('click', fn));

function setBackstage(open: boolean) {
  document.body.classList.toggle('show-backstage', open);
  if (open) document.dispatchEvent(new Event('backstage:shown'));
  all('backstage').forEach((b) => b.setAttribute('aria-pressed', String(open)));
  try { localStorage.setItem('vl-backstage', open ? '1' : '0'); } catch { /* private mode */ }
}
on('backstage', () => setBackstage(!document.body.classList.contains('show-backstage')));
$('#btn-backstage-close').addEventListener('click', () => setBackstage(false));
document.addEventListener('backstage:open', () => setBackstage(true));

on('sound', () => {
  const enabled = !settings.sound;
  sfx.setEnabled(enabled);
  all('sound').forEach((b) => { b.setAttribute('aria-pressed', String(enabled)); b.textContent = enabled ? 'Geluid aan' : 'Geluid uit'; });
});
on('speed', () => {
  const fast = settings.pace === 1;
  settings.pace = fast ? 0.35 : 1;
  all('speed').forEach((b) => { b.setAttribute('aria-pressed', String(fast)); b.textContent = fast ? 'Tempo: snel' : 'Tempo: normaal'; });
});
on('restart', () => location.reload());

// On a phone: the lock icon in the chat header and a small button on the landing page open
// the backstage panel (for the presenter; a real player would never see it).
$('.chat__lock').addEventListener('click', () => setBackstage(true));
const isPhone = matchMedia('(max-width: 720px)').matches;
if (isPhone) {
  const link = el('button.pill', { type: 'button', style: 'margin-top:14px' }, 'Achter de schermen');
  link.addEventListener('click', () => setBackstage(true));
  $('.landing__content').append(link);
}
let remembered: string | null = null;
try { remembered = localStorage.getItem('vl-backstage'); } catch { /* ignore */ }
if (!isPhone && (remembered === '1' || (remembered === null && matchMedia('(min-width: 1100px)').matches))) setBackstage(true);

// --- Laptop / desktop: hand off to the phone -----------------------------------------------------
// This is a smartphone game. On a big screen with a mouse we first show a QR code;
// the phone frame stays available as "presentation mode" (projector, screen sharing).
async function showHandoff() {
  const box = $('#handoff');
  box.hidden = false;
  let url = location.origin;
  let tunnel = false;
  try {
    const r = await (await fetch('/api/public-url')).json() as { url: string | null };
    if (r.url) { url = r.url; tunnel = true; }
  } catch { /* ignore */ }
  const local = ['localhost', '127.0.0.1'].includes(new URL(url).hostname);
  const qr = qrcode(0, 'M');
  qr.addData(url + '/');
  qr.make();
  $('#handoff-qr').innerHTML = local ? '' : qr.createSvgTag({ cellSize: 6, margin: 0, scalable: true });
  $('#handoff-url').textContent = local ? 'Nog geen adres voor je gsm' : url.replace(/^https?:\/\//, '');
  $('#handoff-tip').innerHTML = local
    ? 'Start de demo met <code>./start.sh</code>: dat maakt een HTTPS-adres (nodig voor sensoren op de gsm) en toont hier de QR-code.'
    : tunnel || location.protocol === 'https:' ? 'Tip: in Safari kun je het spel daarna op je beginscherm zetten (Deel → Zet op beginscherm). Het heet daar “Kanaal”.' : 'Let op: zonder HTTPS werken de bewegingssensor en het beginscherm niet op een iPhone.';
}
const desktop = matchMedia('(pointer: fine) and (min-width: 721px)').matches;
let presenting = false;
try { presenting = sessionStorage.getItem('vl-present') === '1' || new URLSearchParams(location.search).has('present'); } catch { /* ignore */ }
if (desktop && !presenting) void showHandoff();
$('#btn-present').addEventListener('click', () => {
  $('#handoff').hidden = true;
  try { sessionStorage.setItem('vl-present', '1'); } catch { /* ignore */ }
});
on('handoff', () => void showHandoff());

// --- Phone niceties ----------------------------------------------------------------------------
// iOS shrinks the *visual* viewport when the keyboard opens; keep the composer above it.
const vv = window.visualViewport;
if (vv && isPhone) {
  const fit = () => {
    document.documentElement.style.setProperty('--app-height', `${vv.height}px`);
    window.scrollTo(0, 0);
  };
  vv.addEventListener('resize', fit);
  fit();
}
// Installable web app (home screen) with an app-shell cache. Never caches /api/.
if ('serviceWorker' in navigator && (location.protocol === 'https:' || location.hostname === 'localhost')) {
  addEventListener('load', () => void navigator.serviceWorker.register('/sw.js').catch(() => {}));
}
const standalone = isStandalone();

void backstage.initModelBox();

// Preload the task images, so the hidden message can appear while the phone is offline
for (const src of ['/assets/door.jpg', '/assets/symbol.jpg']) { const img = new Image(); img.src = src; void img.decode?.().catch(() => {}); }

// --- Start ----------------------------------------------------------------------------------------
const story = new Story();

$('#btn-begin').addEventListener('click', async () => {
  sfx.unlock(); // audio and (on iOS) sensors need a tap first
  backstage.reset();
  story.state.startedAt = Date.now();
  try {
    const s = await api.session();
    story.state.session = s.id;
    backstage.log('privacy', 'Anonieme sessie', `Spelerscode <code>${s.id.slice(0, 8)}…</code> aangemaakt. Geen formulier, geen account: naam en e-mail volgen pas in het verhaal (<code>onboarding.md</code>).`);
  } catch {
    backstage.log('privacy', 'Geen server', 'De demo speelt verder zonder sessie.');
  }
  backstage.log('sensor', standalone ? 'Gestart als webapp' : 'Gestart in de browser', standalone ? 'Geopend vanaf het beginscherm: <code>display-mode: standalone</code>. Geen adresbalk, eigen icoon (“Kanaal”), en op iOS zijn pushmeldingen mogelijk.' : 'Gewone browsertab (<code>display-mode: browser</code>).');
  // Not installed on a phone: ask for it (Android: system dialog) or show the steps (iOS)
  if (!standalone && detect().platform !== 'desktop') {
    await askToInstall({ label: 'Voor je begint', title: 'Speel Kanaal als app', quote: 'Het spel werkt het best vanaf je beginscherm, zoals een echte app. Het duurt tien seconden.' });
  }
  try { await (navigator as Navigator & { wakeLock?: { request(t: 'screen'): Promise<unknown> } }).wakeLock?.request('screen'); } catch { /* optional */ }

  show('signal');
  await runSignal();
  show('chat');
  await sleep(500);
  await story.run();
});

// --- Opened from a notification ------------------------------------------------------------------------
const params = new URLSearchParams(location.search);
const resume = params.get('resume');
if (resume) {
  history.replaceState(null, '', '/');
  const sentAt = Number(params.get('sent')) || null;
  if (resume === 'push') {
    $('#handoff').hidden = true;
    show('chat');
    void story.resumeFromPush(sentAt);
  } else {
    clearBadge();
    setBackstage(true);
    backstage.log('sensor', 'Testmelding geopend (koude start)', `De app was gesloten; de melding opende ze opnieuw via <code>clients.openWindow</code>${sentAt ? `, ${Math.round((Date.now() - sentAt) / 1000)} s na verzenden` : ''}.`);
  }
}
// Warm start for test notifications (story notifications are handled inside the story)
onPushOpen((e) => {
  if (e.kind !== 'test') return;
  clearBadge();
  setBackstage(true);
  backstage.log('sensor', 'Testmelding geopend (warme start)', `De app zat nog in het geheugen; de service worker bracht ze naar voren${e.sentAt ? `, ${Math.round((e.openedAt - e.sentAt) / 1000)} s na verzenden` : ''}.`);
});

// --- Backstage: install and push status + test button ----------------------------------------------
let testSession: string | undefined;
async function renderPwaStatus() {
  const info = detect();
  const perm = permission();
  const support = pushSupport();
  const permText = { granted: 'toegestaan', denied: 'geweigerd', default: 'nog niet gevraagd', 'n/a': 'niet beschikbaar' }[perm];
  const supportText = { ok: 'ondersteund', 'needs-install': 'enkel na installatie (iOS)', unsupported: 'niet ondersteund', insecure: 'vereist HTTPS' }[support];
  $('#pwa-status').innerHTML = [
    `Geïnstalleerd: <b>${info.standalone ? 'ja' : 'nee'}</b> (${info.platform}, ${info.browser}${info.inApp ? `, in ${info.inApp}` : ''})`,
    `Push: <b>${supportText}</b> · meldingen: <b>${permText}</b>`,
  ].join('<br>');
  ($('#btn-push-test') as HTMLButtonElement).disabled = support !== 'ok';
}
$('#btn-push-test').addEventListener('click', async () => {
  const out = $('#push-test-result');
  testSession ??= story.state.session ?? (await api.session().catch(() => ({ id: undefined }))).id;
  if (!testSession) { out.textContent = 'Geen verbinding met de server.'; return; }
  out.textContent = 'Toestemming vragen…';
  const sub = await subscribe(testSession);
  void renderPwaStatus();
  if (!sub.ok) { out.textContent = sub.reason === 'denied' ? 'Meldingen geweigerd. Zet ze aan in de instellingen van je toestel.' : 'Abonneren mislukt.'; return; }
  const since = Date.now();
  const ok = await schedule(testSession, 'test', 10);
  out.textContent = ok ? 'Melding over 10 s. Sluit nu de app en tik op de melding.' : 'Plannen mislukt.';
  backstage.log('sensor', 'Testmelding gepland', `Abonnement bij <code>${sub.service}</code>. Over 10 s stuurt de server een melding; tik erop om te zien of de app terug opent.`);
  if (!ok) return;
  const r = await deliveryResult(testSession, since, 30000);
  if (!r) return;
  out.textContent = r.ok ? `Afgeleverd bij ${r.service} (HTTP ${r.status}).` : `Geweigerd: ${explainFailure(r)}`;
  backstage.log('sensor', r.ok ? 'Testmelding afgeleverd' : 'Testmelding geweigerd', `${r.service}: HTTP ${r.status ?? '–'}${r.reason ? ` <code>${r.reason}</code>` : ''}. VAPID subject: <code>${r.subject}</code>.`);
});
void renderPwaStatus();
document.addEventListener('backstage:shown', () => void renderPwaStatus());
document.addEventListener('visibilitychange', () => { if (!document.hidden) void renderPwaStatus(); });

// A player who leaves in the middle of a conversation is noticed (Page Visibility API)
let hiddenAt = 0, remarked = false;
document.addEventListener('visibilitychange', () => {
  if (document.hidden) { hiddenAt = Date.now(); return; }
  const away = Date.now() - hiddenAt;
  const waiting = !!$('#chat-composer').firstElementChild && $('#sheet').hidden;
  if (!remarked && hiddenAt && away > 4000 && views.chat.classList.contains('is-active') && waiting && story.state.firstName) {
    remarked = true;
    void story.chat.say('joris', `Je was plots weg, ${story.state.firstName}. Alles oké?`, { pause: 800 });
    backstage.log('sensor', 'Speler was even weg', `Page Visibility API: ${Math.round(away / 1000)} s weg uit de app. Het personage merkt het op, één keer.`);
  }
});
